import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { Loader } from 'semantic-ui-react';

import ModalTypes from '../../constants/ModalTypes';
import FixedContainer from '../../containers/FixedContainer';
import StaticContainer from '../../containers/StaticContainer';
import UsersModalContainer from '../../containers/UsersModalContainer';
import UserSettingsModalContainer from '../../containers/UserSettingsModalContainer';
import ProjectAddModalContainer from '../../containers/ProjectAddModalContainer';
// FAMILY-EXT: внешние хранилища
import ExternalStoragesModalContainer from '../../containers/ExternalStoragesModalContainer';
import Background from '../Background';
// FAMILY-EXT: feature 1
import { playReturnNotification } from '../../utils/family-notification-sound';
// FAMILY-EXT: матрица Эйзенхауэра
import EisenhowerQuadrantPickerContainer from '../../containers/EisenhowerQuadrantPickerContainer';

import styles from './Core.module.scss';

const Core = React.memo(
  ({
    isInitializing,
    isSocketDisconnected,
    currentModal,
    currentProject,
    currentBoard,
    // FAMILY-EXT: feature 1
    returnedCardsCount,
  }) => {
    const [t] = useTranslation();

    const defaultTitle = useRef(document.title);
    // FAMILY-EXT: feature 1 — отслеживаем рост счётчика, чтобы пинькать
    // только на новых "возвратах", а не каждый рендер.
    const prevReturnedCountRef = useRef(0);

    // Звук при появлении новых вернувшихся карточек.
    useEffect(() => {
      if (returnedCardsCount > prevReturnedCountRef.current) {
        playReturnNotification();
      }
      prevReturnedCountRef.current = returnedCardsCount;
    }, [returnedCardsCount]);

    useEffect(() => {
      let baseTitle;
      if (currentProject) {
        baseTitle = currentProject.name;

        if (currentBoard) {
          baseTitle += ` | ${currentBoard.name}`;
        }
      } else {
        baseTitle = defaultTitle.current;
      }

      // FAMILY-EXT: feature 1 — если есть вернувшиеся карточки, мигаем title
      // двумя вариантами с интервалом, чтобы во вкладке было заметно.
      if (returnedCardsCount === 0) {
        document.title = baseTitle;
        return undefined;
      }

      const flashTitle = `(${returnedCardsCount}) ⚠ Вернулась карточка!`;
      document.title = flashTitle;

      let phase = 1;
      const intervalId = setInterval(() => {
        phase = (phase + 1) % 2;
        document.title = phase === 0 ? baseTitle : flashTitle;
      }, 1500);

      return () => {
        clearInterval(intervalId);
        document.title = baseTitle;
      };
    }, [currentProject, currentBoard, returnedCardsCount]);

    return (
      <>
        {isInitializing ? (
          <Loader active size="massive" />
        ) : (
          <>
            {currentProject && currentProject.background && (
              <Background
                type={currentProject.background.type}
                name={currentProject.background.name}
                imageUrl={currentProject.backgroundImage && currentProject.backgroundImage.url}
              />
            )}
            <FixedContainer />
            <StaticContainer />
            {currentModal === ModalTypes.USERS && <UsersModalContainer />}
            {currentModal === ModalTypes.USER_SETTINGS && <UserSettingsModalContainer />}
            {currentModal === ModalTypes.PROJECT_ADD && <ProjectAddModalContainer />}
            {/* FAMILY-EXT: модал внешних хранилищ */}
            {currentModal === ModalTypes.EXTERNAL_STORAGES && <ExternalStoragesModalContainer />}
            {/* FAMILY-EXT: матрица Эйзенхауэра — picker сам решает, рендериться
                ли (внутри он null-out'ит когда pendingPicker отсутствует). */}
            <EisenhowerQuadrantPickerContainer />
          </>
        )}
        {isSocketDisconnected && (
          <div className={styles.message}>
            <div className={styles.messageHeader}>{t('common.noConnectionToServer')}</div>
            <div className={styles.messageContent}>
              <Trans i18nKey="common.allChangesWillBeAutomaticallySavedAfterConnectionRestored">
                All changes will be automatically saved
                <br />
                after connection restored
              </Trans>
            </div>
          </div>
        )}
      </>
    );
  },
);

Core.propTypes = {
  isInitializing: PropTypes.bool.isRequired,
  isSocketDisconnected: PropTypes.bool.isRequired,
  currentModal: PropTypes.oneOf(Object.values(ModalTypes)),
  /* eslint-disable react/forbid-prop-types */
  currentProject: PropTypes.object,
  currentBoard: PropTypes.object,
  /* eslint-enable react/forbid-prop-types */
  // FAMILY-EXT: feature 1
  returnedCardsCount: PropTypes.number.isRequired,
};

Core.defaultProps = {
  currentModal: undefined,
  currentProject: undefined,
  currentBoard: undefined,
};

export default Core;
